import type { VisualizationPipeline } from '../../RunPipeline.js'
import { FilterEditorRow } from './FilterEditorRow.js'
import { GroupByEditorRow } from './GroupByEditorRow.js'
import type { MetadataFields, Metadatafieldtypes } from '../../MetadataFields.js'
import type { FilterOperationKey, GroupByOperationKey, ProjectionOperationKey } from '../../OperationRegistry.js'
import { OPERATIONS } from '../../OperationRegistry.js'
import { useMemo, useState } from 'react'
import { categorizeFields } from './FieldCategories.js'
import { type BoundOperation, isFilterOperation } from '../../OperationTypes.js'
import type { VisualizationSampleContainer } from '../../VisualizationSampleContainer.js'
import type { Mode } from '../GroupingPanel.tsx'

type Props = {
  samples: VisualizationSampleContainer[]
  pipeline: VisualizationPipeline
  setPipeline: (p: VisualizationPipeline) => void
  metadataFields: MetadataFields
  mode?: Mode
}
export function CompactBasicModeEditor({ samples, pipeline, setPipeline, metadataFields, mode }: Props) {
  const singleFilter = pipeline.filters[0]
  const groupBy = pipeline.groupBys[0]
  const projection = pipeline.projections.find((p) => p.metaDataField === groupBy?.metaDataField)

  const [draftFilterField, setDraftFilterField] = useState<Metadatafieldtypes>()

  const handleFilterChange = <T extends FilterOperationKey>(next: BoundOperation<T, unknown>) => {
    setPipeline({ ...pipeline, filters: [next] })
    setDraftFilterField(undefined)
  }

  const handleFilterRemove = () => {
    setPipeline({ ...pipeline, filters: [] })
  }

  const handleGroupByChange = (groupConfig: {
    groupBy: BoundOperation<GroupByOperationKey, unknown>
    projection?: BoundOperation<ProjectionOperationKey, unknown>
  }) => {
    const updatedProjections = pipeline.projections.filter((p) => p.metaDataField !== groupConfig.groupBy.metaDataField)
    if (groupConfig.projection) {
      updatedProjections.push(groupConfig.projection)
    }

    setPipeline({
      ...pipeline,
      groupBys: [groupConfig.groupBy],
      projections: updatedProjections
    })
  }

  const handleStartFilterSetup = (fieldKey: Metadatafieldtypes) => {
    const descriptor = metadataFields[fieldKey]
    const op = OPERATIONS.filter((op) => isFilterOperation(op)).find((op) => op.applicableTo.includes(descriptor.type))

    if (op) {
      setPipeline({
        ...pipeline,
        filters: [
          {
            operationKey: op.key,
            metaDataField: fieldKey,
            config: op.config?.defaultConfig ?? undefined
          }
        ]
      })
      setDraftFilterField(undefined)
    }
  }

  const filterableFields = useMemo(() => {
    return categorizeFields(metadataFields, samples)
  }, [samples])

  return (
    <div className="flex flex-row gap-3">
      <div>
        {mode !== 'only-filter' && <span className="text-sm font-medium">Filter:</span>}
        {singleFilter ? (
          <FilterEditorRow
            metadataFields={metadataFields}
            boundFilterOperation={singleFilter}
            onChange={handleFilterChange}
            onRemove={handleFilterRemove}
            samples={samples}
          />
        ) : (
          <div className="flex items-center gap-2">
            <select
              className="select select-sm select-bordered"
              value={draftFilterField}
              onChange={(e) => setDraftFilterField(e.target.value as Metadatafieldtypes)}
            >
              <option value="">➕ Select field to filter</option>
              {Object.entries(filterableFields.other).map(([fieldKey, desc]) => (
                <option key={fieldKey} value={fieldKey}>
                  {desc.label}
                </option>
              ))}
            </select>
            {draftFilterField && (
              <button className="btn btn-sm btn-primary" onClick={() => handleStartFilterSetup(draftFilterField)}>
                Add
              </button>
            )}
          </div>
        )}
      </div>

      {mode !== 'only-filter' && (
        <div>
          <span className="text-sm font-medium">Group by:</span>
          <GroupByEditorRow
            metadataFields={metadataFields}
            value={{ groupBy: groupBy ?? { key: '', field: '', config: {} }, projection }}
            onChange={handleGroupByChange}
            onRemove={() => setPipeline({ ...pipeline, groupBys: [], projections: [] })}
            samples={samples}
          />
        </div>
      )}
    </div>
  )
}
